import React from 'react';
import { ImageMedia } from './ImageMedia';
import { VideoMedia } from './VideoMedia';
import { MediaObject } from 'src/api/services/MediaClient';
import { SVGMedia } from './SVGMedia';
import { AudioMedia } from './AudioMedia';
import { If } from 'src/components/If';
import { decorateMedia } from 'src/features/media';
import { FileIcon } from '../FileIcon';
import { FileVideo2, Music } from 'lucide-react';
import { cn } from 'src/lib/utils';

type Props = {
  media: MediaObject;
  playable?: boolean;
  renderOriginal?: boolean;
} & Omit<React.ComponentPropsWithoutRef<'img' | 'video'>, 'src' | 'poster' | 'controls'>;

// TODO: broken images/videos
const MediaViewer: React.FC<Props> = ({
  media,
  playable = false,
  renderOriginal = false,
  ...props
}) => {
  const file = decorateMedia(media);

  const canRenderVideoAsImage = !!(file?.isVideo && file?.hasThumbnail);

  const shouldRenderImage = file?.isImage || (canRenderVideoAsImage && !playable);
  const shouldRenderSvg = file?.isSvg && (renderOriginal || !file?.hasThumbnail);
  const shouldRenderVideo = file?.isVideo;
  const shouldRenderAudio = file?.isAudio && file?.canPlay;

  const imageSrc = renderOriginal ? file?.index_url : (file?.thumbnail ?? file?.index_url);



  const isArchived = media?.archived_at || (file?.isVideo && !media.video);

  return isArchived ? (
    <div
      className={
        'tw-flex tw-h-12 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-[#c0c0c0]'
      }
    >
      <FileVideo2 className={'tw-text-text-subtle'} size={20} />
    </div>
  ) : (
    <If
      when={!file?.isUnknown}
      else={
        <FileIcon
          mimeType={file?.mime_type ?? ''}
          className={cn('tw-mx-auto tw-h-12 tw-w-12', props.className)}
        />
      }
    >
      <If
        when={shouldRenderImage}
        else={
          <If
            when={shouldRenderVideo}
            else={
              <If when={shouldRenderAudio}>
                <AudioMedia
                  wrapperProps={{
                    className: cn('tw-flex tw-w-full tw-flex-col', props.className),
                  }}
                  src={file?.index_url ?? ''}
                  poster={
                    <div
                      className={
                        'align-self-center align-items-center justify-content-center tw-flex tw-w-full tw-grow'
                      }
                    >
                      <Music size={20} />
                    </div>
                  }
                  controls={playable}
                />
              </If>
            }
          >
            <VideoMedia
              src={file?.index_url ?? ''}
              mime_type={file?.mime_type}
              poster={file?.poster ?? file?.index_url}
              controls={playable || !canRenderVideoAsImage}
              {...(props as any)}
            />
          </If>
        }
      >
        <If when={shouldRenderSvg} else={<ImageMedia src={imageSrc ?? ''} {...props} />}>
          <SVGMedia src={imageSrc ?? ''} {...(props as any)} />
        </If>
      </If>
    </If>
  );
};

export type { Props as MediaViewerProps };
export { MediaViewer };
